/** Map backend voice-profile errors to actionable translation keys.
 *
 * The API answers with a stable `detail` slug (see the routers in
 * `apps/api2/routers/v3/{speaker,document}/voice_profile*.py`). Showing the
 * slug would be useless to an editor, and showing a generic "something went
 * wrong" hides the one thing they can act on — the selection is too short, the
 * media is gone, the limit is reached. Translate the slug, fall back to the
 * status code, and only then to a generic message. */

import { AxiosError } from 'axios';

/** Slug → translation key. Keys live in `voice-error-*` namespaces. */
const DETAIL_KEYS: Record<string, string> = {
  // Selection problems the user can fix by choosing different speech.
  'voice-sample-insufficient-clean-speech':
    'voice-error-insufficient-clean-speech',
  'voice-sample-invalid-selection': 'voice-error-invalid-selection',
  // Consent / permission.
  'voice-consent-required': 'voice-error-consent-required',
  // Capacity and limits.
  'voice-profile-maximum-samples': 'voice-error-sample-limit',
  'voice-sample-too-large': 'voice-error-sample-too-large',
  'voice-profile-multiple': 'voice-error-multiple-samples',
  // Media / source availability.
  'voice-sample-media-unavailable': 'voice-error-media-unavailable',
  'voice-source-results-unavailable': 'voice-error-no-diarization',
  'retained-finished-session-required': 'voice-error-session-not-retained',
  // Transient server conditions.
  'voice-sample-processing-busy': 'voice-error-busy',
  'voice-sample-temporarily-unavailable': 'voice-error-temporarily-unavailable',
  'voice-model-unavailable': 'voice-error-model-unavailable',
  'voice-model-not-configured': 'voice-error-not-configured',
  'voice-key-not-configured': 'voice-error-not-configured',
  'voice-threshold-not-configured': 'voice-error-not-calibrated',
  'voice-threshold-invalid': 'voice-error-not-calibrated',
  // Feature / lookup.
  'voice-profiles-not-enabled': 'voice-error-not-enabled',
  'voice-profile-not-found': 'voice-error-sample-not-found',
  'voice-identification-not-found': 'voice-error-label-not-found',
  'speaker-not-found': 'voice-error-speaker-not-found'
};

/** Status code → translation key, for responses without a known slug. */
const STATUS_KEYS: Record<number, string> = {
  400: 'voice-error-invalid-selection',
  403: 'voice-error-not-permitted',
  404: 'voice-error-not-enabled',
  409: 'voice-error-conflict',
  413: 'voice-error-sample-too-large',
  429: 'voice-error-rate-limited',
  503: 'voice-error-temporarily-unavailable'
};

/** The API nests its message differently per route; check every shape. */
const readDetail = (error: unknown): string | undefined => {
  let data = (error as AxiosError<Record<string, unknown>>)?.response?.data;
  // Preview requests use arraybuffer for WAV bytes, including JSON failures.
  // Decode the error body so busy/media errors keep their useful explanation.
  if (Object.prototype.toString.call(data) === '[object ArrayBuffer]') {
    try {
      const parsed: unknown = JSON.parse(
        new TextDecoder().decode(data as unknown as ArrayBuffer)
      );
      data =
        parsed && typeof parsed === 'object'
          ? (parsed as Record<string, unknown>)
          : undefined;
    } catch {
      return undefined;
    }
  }
  if (!data) return undefined;
  const candidate =
    (typeof data.detail === 'string' && data.detail) ||
    (typeof data.error_description === 'string' && data.error_description) ||
    (typeof data.error === 'string' && data.error);
  return candidate || undefined;
};

/** Translation key for any voice-profile request failure. */
export const voiceErrorKey = (error: unknown): string => {
  if (!error) return 'voice-error-generic';
  const detail = readDetail(error);
  if (detail && DETAIL_KEYS[detail]) return DETAIL_KEYS[detail];
  const status = (error as AxiosError)?.response?.status;
  if (status && STATUS_KEYS[status]) return STATUS_KEYS[status];
  return 'voice-error-generic';
};

/** True when retrying the identical request could plausibly succeed.
 *  Busy/transient conditions are serialized per API process, so a retry is
 *  the documented remedy; a 400 selection error is not. */
export const isRetryableVoiceError = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status;
  if (status === 429 || status === 503) return true;
  const detail = readDetail(error);
  return (
    detail === 'voice-sample-processing-busy' ||
    detail === 'voice-sample-temporarily-unavailable'
  );
};

/** True when the save may or may not have landed, so the UI must re-read
 *  sample metadata before offering a retry (issue #2355). A network error or
 *  gateway failure leaves the outcome genuinely unknown. */
export const isUncertainVoiceSave = (error: unknown): boolean => {
  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;
  if (!axiosError?.isAxiosError) return false;
  // No response at all: the request may have reached the server anyway.
  if (!axiosError.response) return true;
  return status === 502 || status === 504;
};
